import { z } from 'zod/v3';
import { AssertionSourceSchema } from '../generated/enums';
import { SlaEvaluatedItemDTOSchema } from './sla.dto';

/**
 * RFC-180 M1 — CCCE (Condition / Criteria / Cause / Effect) is the
 * customer-facing structured finding format. Each field is independently
 * nullable so partial CCCE can render until all four are populated.
 *
 * Persisted as columns on `RemediationTicket`. The shape borrows from
 * auditor methodology but the consumer is the founder/CTO; auditors do
 * their own findings.
 */
export const RemediationTicketCcceSchema = z.object({
  condition: z.string().nullable(),
  criteria: z.string().nullable(),
  cause: z.string().nullable(),
  effect: z.string().nullable(),
});
export type RemediationTicketCcce = z.infer<typeof RemediationTicketCcceSchema>;

export const CesFindingDTOSchema = z.object({
  id: z.string(),
  programId: z.string(),
  findingKey: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  severity: z.string().nullable(),
  controlRef: z.string().nullable(),
  /** Controls-tree code (`AUD-02`) for display; `controlRef` stays the key. */
  controlDisplayCode: z.string().nullable().optional(),
  status: z.string(),
  sourceType: z.string().nullable(),
  sourceDetail: z.string().nullable(),
  firstDetected: z.string(),
  lastDetected: z.string(),
  ticketUrl: z.string().nullable(),
  ccce: RemediationTicketCcceSchema,
  /**
   * Backlink to the RiskItem this finding was materialized into
   * (RFC-142, via `RiskItem.sourceRef = "remediation-ticket:<id>"`).
   * Null when no risk has been materialized from this finding.
   */
  linkedRisk: z
    .object({ id: z.string(), displayId: z.string(), invalidatedAt: z.string().nullable().optional() })
    .nullable()
    .optional(),
  slaEvaluation: SlaEvaluatedItemDTOSchema.nullable().optional(),
});
export type CesFindingDTO = z.infer<typeof CesFindingDTOSchema>;

/**
 * RFC-180 M1 — Four-axis evidence quality scoring, 1:1 with `EvidenceFile`.
 * Each axis is 0–3 (3 = perfect, 0 = absent). Customer-facing — surfaces
 * in CES detail and the preflight render so the founder can self-correct
 * before the auditor sees the evidence.
 */
export const EvidenceFileValidationSchema = z.object({
  id: z.string(),
  evidenceFileId: z.string(),
  completeness: z.number().int().min(0).max(3),
  timeliness: z.number().int().min(0).max(3),
  relevance: z.number().int().min(0).max(3),
  authenticity: z.number().int().min(0).max(3),
  notes: z.string().nullable(),
  validatedBy: z.string(),
  validatedAt: z.string(), // ISO 8601
});
export type EvidenceFileValidation = z.infer<typeof EvidenceFileValidationSchema>;

export const RemediationClaimCandidateSchema = z.object({
  id: z.string(),
  claimText: z.string(),
  policyName: z.string(),
  systemOfRecord: z.string(),
  verification: z.string(),
  controlRef: z.string().nullable(),
  stale: z.boolean(),
  assertionSource: AssertionSourceSchema.nullable(),
  lastVerified: z.string().nullable(),
});
export type RemediationClaimCandidate = z.infer<typeof RemediationClaimCandidateSchema>;

export const RemediationClaimCandidatesResponseSchema = z.object({
  ticketId: z.string(),
  programId: z.string(),
  controlRef: z.string().nullable(),
  affectedClaimIds: z.array(z.string()),
  claims: z.array(RemediationClaimCandidateSchema),
});
export type RemediationClaimCandidatesResponse = z.infer<typeof RemediationClaimCandidatesResponseSchema>;
